import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle, Sparkles } from 'lucide-react';
import { useIsVerified } from '@/hooks/use-verification';

/**
 * Welcome modal that shows once when a user gets verified
 * Uses localStorage to track if they've seen it
 */
export const VerifiedWelcomeModal: React.FC = () => {
  const { isVerified, isLoading } = useIsVerified();
  const [showWelcome, setShowWelcome] = useState(false);
  
  useEffect(() => {
    // Don't show while loading or if not verified
    if (isLoading || !isVerified) {
      return;
    }
    
    // Check if user has already seen the welcome message
    const hasSeenWelcome = localStorage.getItem('has-seen-verified-welcome') === 'true';
    
    if (!hasSeenWelcome) {
      // Small delay for better UX (let the page load first)
      const timer = setTimeout(() => {
        setShowWelcome(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isVerified, isLoading]);
  
  const handleContinue = () => {
    // Mark as seen in localStorage
    localStorage.setItem('has-seen-verified-welcome', 'true');
    setShowWelcome(false);
  };
  
  return (
    <Dialog open={showWelcome} onOpenChange={(open) => {
      if (!open) handleContinue();
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          
          <DialogTitle className="text-center text-2xl">
            🎉 Welcome to FOMO!
          </DialogTitle>
          
          <DialogDescription className="text-center space-y-3">
            <div className="flex items-center justify-center gap-2 text-base font-semibold text-green-600 dark:text-green-400">
              <Sparkles className="w-5 h-5" />
              <span>You're Verified Human!</span>
              <Sparkles className="w-5 h-5" />
            </div>
            
            <p className="text-muted-foreground">
              Your verification has been approved! You now have full access to all features.
            </p>
            
            <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
              <p className="font-medium">✨ You can now:</p>
              <ul className="text-left space-y-1 text-muted-foreground">
                <li>• Create and share bulletins</li>
                <li>• Upload photos and videos</li>
                <li>• Comment and interact</li>
                <li>• Join communities</li>
                <li>• Collect drops</li>
              </ul>
            </div>
          </DialogDescription>
        </DialogHeader>
        
        <DialogFooter className="sm:justify-center">
          <Button 
            onClick={handleContinue}
            className="w-full sm:w-auto px-8"
            size="lg"
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
